import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Permissions } from '@/auth/permissions';
import { useHasPermission } from '@/auth/RequirePermission';
import { CompanySettings } from '@/routes/settings/CompanySettings';
import { SettingsNav } from '@/routes/settings/SettingsNav';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { FIELD_CLASS } from '@/components/properties/AddressFields';
import {
  DuplicateRoleLabelError,
  useCreatePartyRelationshipRole,
  usePartyRelationshipRoles,
  useSetPartyRelationshipRoleActive,
  useUpdatePartyRelationshipRole,
  type RelationshipRole,
} from '@/routes/parties/useParties';

/**
 * Account-wide settings. M1-25 gave it the relationship-role catalog; M2-19
 * made it the company's own settings — profile, logo, hours and timezone, and
 * who changed them — which is what the route is gated on now
 * (`company.manage`, Owner and Admin).
 *
 * ⚠️ The relationship-role catalog stays Owner-only. Its API is
 * `account.manage`, which the M0-22 matrix gives the Owner alone, so an Admin
 * reaching this page through `company.manage` is not shown a section whose
 * every write would be refused.
 */
export function Settings() {
  const { t } = useTranslation();
  const hasPermission = useHasPermission();

  return (
    <section className="space-y-6">
      <h1 className="text-2xl font-semibold tracking-tight">{t('nav.settings')}</h1>
      <SettingsNav />
      <CompanySettings />
      {hasPermission(Permissions.manageAccount) ? <RelationshipRolesSection /> : null}
    </section>
  );
}

function RelationshipRolesSection() {
  const { t } = useTranslation();
  const roles = usePartyRelationshipRoles(true);
  const createRole = useCreatePartyRelationshipRole();
  const updateRole = useUpdatePartyRelationshipRole;
  const setActive = useSetPartyRelationshipRoleActive;

  const [newLabel, setNewLabel] = useState('');
  const [error, setError] = useState('');

  function addRole() {
    const label = newLabel.trim();
    if (!label) {
      return;
    }
    setError('');
    createRole.mutate(label, {
      onSuccess: () => setNewLabel(''),
      onError: (err) =>
        setError(err instanceof DuplicateRoleLabelError ? err.message : t('settings.relationshipRoles.addFailed')),
    });
  }

  if (roles.isPending) {
    return <LoadingState label={t('settings.relationshipRoles.loading')} />;
  }

  if (roles.isError) {
    return (
      <ErrorState
        title={t('settings.relationshipRoles.loadErrorTitle')}
        onRetry={() => void roles.refetch()}
      />
    );
  }

  // sortOrder round-trips as a string as often as a number (the generated
  // client types it `number | string`, the same reason PropertyDetail's own
  // latitude/longitude need `toNumericCoordinate`) — coerced here since this
  // is the only place order actually matters.
  const ordered = [...roles.data].sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder));

  return (
    <div className="space-y-3 rounded-lg border p-4">
      <div>
        <h2 className="text-base font-semibold">{t('settings.relationshipRoles.heading')}</h2>
        <p className="text-sm text-[var(--color-muted-foreground)]">
          {t('settings.relationshipRoles.description')}
        </p>
      </div>

      <ul className="space-y-1" data-testid="relationship-roles-list">
        {ordered.map((role) => (
          <RoleRow key={role.id} role={role} onUpdate={updateRole} onSetActive={setActive} />
        ))}
      </ul>

      <div className="flex items-end gap-2 border-t border-[var(--color-border)] pt-3">
        <label className="block flex-1 text-sm">
          <span className="mb-1 block font-medium">{t('settings.relationshipRoles.newLabel')}</span>
          <input
            className={FIELD_CLASS}
            value={newLabel}
            onChange={(event) => setNewLabel(event.target.value)}
          />
        </label>
        <Button type="button" size="sm" disabled={!newLabel.trim() || createRole.isPending} onClick={addRole}>
          {t('settings.relationshipRoles.add')}
        </Button>
      </div>
      {error ? (
        <p role="alert" className="text-sm text-[var(--color-destructive)]">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** One catalog row — rename inline, retire/restore. New roles append at the end. */
function RoleRow({
  role,
  onUpdate,
  onSetActive,
}: {
  role: RelationshipRole;
  onUpdate: typeof useUpdatePartyRelationshipRole;
  onSetActive: typeof useSetPartyRelationshipRoleActive;
}) {
  const { t } = useTranslation();
  const update = onUpdate(role.id);
  const setActive = onSetActive(role.id);
  const [label, setLabel] = useState(role.label);
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Moves focus into the field once it appears, without the a11y pitfalls of
  // the declarative `autoFocus` prop (which can steal focus regardless of
  // how the element came to exist) — this only runs in reaction to the
  // person's own "Rename" click.
  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
    }
  }, [editing]);

  function save() {
    const trimmed = label.trim();
    if (!trimmed || trimmed === role.label) {
      setEditing(false);
      setLabel(role.label);
      return;
    }
    update.mutate(
      { label: trimmed, sortOrder: Number(role.sortOrder) },
      { onSuccess: () => setEditing(false) },
    );
  }

  return (
    <li
      data-testid="relationship-role-row"
      className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-[var(--color-border)] px-3 py-2 text-sm"
    >
      {editing ? (
        <span className="flex flex-1 items-center gap-2">
          <input
            ref={inputRef}
            className={FIELD_CLASS}
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                save();
              }
              if (event.key === 'Escape') {
                setEditing(false);
                setLabel(role.label);
              }
            }}
          />
          <button type="button" className="text-xs underline underline-offset-2" onClick={save}>
            {t('common.save')}
          </button>
        </span>
      ) : (
        <span className={role.isActive ? '' : 'text-[var(--color-muted-foreground)] line-through'}>
          {role.label}
        </span>
      )}

      <span className="flex gap-3">
        {!editing ? (
          <button type="button" className="text-xs underline underline-offset-2" onClick={() => setEditing(true)}>
            {t('settings.relationshipRoles.rename')}
          </button>
        ) : null}
        <button
          type="button"
          className="text-xs underline underline-offset-2"
          disabled={setActive.isPending}
          onClick={() => setActive.mutate(!role.isActive)}
        >
          {role.isActive
            ? t('settings.relationshipRoles.deactivate')
            : t('settings.relationshipRoles.activate')}
        </button>
      </span>
    </li>
  );
}
